import { FieldError, FieldErrors } from 'react-hook-form'

function getValueAtPath(root: unknown, path: string) {
  if (!root || !path) return undefined
  // Support both dot and bracket notation: medications.1.name or medications[1].name
  const segments = path
    .replace(/\[(\d+)\]/g, '.$1')
    .split('.')
    .filter(Boolean)
  let current: any = root
  for (const segment of segments) {
    if (current == null) return undefined
    const key = /^\d+$/.test(segment) ? Number(segment) : segment
    current = current?.[key as any]
  }
  return current
}

export function getErrorField<E extends FieldErrors>(
  fieldName: string,
  errors?: E
) {
  // Use type assertion to bypass the index signature error
  const error = errors?.[fieldName as keyof E]
  if (error) {
    return error
  }

  // Attempt to resolve nested errors using the field path
  const nestedError = getValueAtPath(errors, fieldName)
  if (
    nestedError &&
    typeof nestedError === 'object' &&
    // Heuristic to identify FieldError-like objects
    ('message' in nestedError || 'type' in nestedError || 'ref' in nestedError)
  ) {
    return nestedError as FieldError
  }

  // Iterate over the object keys so we can check the nested ref.name field
  for (const key in errors) {
    const typedKey = key as keyof E

    // Type assertion for the ref property
    const potentialError = errors[typedKey] as { ref?: { name: string } }

    if (potentialError.ref?.name === fieldName) {
      return potentialError as FieldError
    }
  }

  return undefined
}

export function prepareFormForSubmission<T>(data: T) {
  const cleanedData: T = {
    ...data,
  }

  for (const key in cleanedData) {
    if (cleanedData[key as keyof typeof cleanedData] === '') {
      // @ts-expect-error - our zod validation layer will ensure the UI can't submit a bad value on our required fields,
      // so the only fields that we'll actually be setting to null are the optional fields that we're unsetting here.
      cleanedData[key as keyof typeof cleanedData] = null
    }
  }

  return cleanedData
}
